import React from 'react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';

import {
  getLatestNewsFromEbc,
  type LatestNewsFromEbcItem,
} from '../../../services/api/latestNewsFromEbc';

import './LatestNewsAside.less';

type LatestNewsAsideProps = {
  title?: string;
  maxItems?: number;
};

function LatestNewsAsideSkeletonItem({ index }: { index: number }) {
  return (
    <article
      key={index}
      className="latest-news-aside__item latest-news-aside__item--loading"
    >
      <div className="latest-news-aside__link latest-news-aside__link--loading">
        <div className="latest-news-aside__loadingIndex shimmer" />
        <div className="latest-news-aside__loadingCopy">
          <div className="latest-news-aside__loadingTitle shimmer" />
          <div className="latest-news-aside__loadingTitle latest-news-aside__loadingTitle--short shimmer" />
        </div>
      </div>
    </article>
  );
}

export default function LatestNewsAside({
  title = 'Notícias Recentes',
  maxItems = 5,
}: LatestNewsAsideProps) {
  const [items, setItems] = React.useState<LatestNewsFromEbcItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);

      try {
        const result = await getLatestNewsFromEbc();

        if (cancelled) return;

        setItems((result || []).slice(0, maxItems));
      } catch (error) {
        if (!cancelled) {
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [maxItems]);

  return (
    <section className="latest-news-aside" aria-busy={loading}>
      <div className="latest-news-aside__card">
        <header className="latest-news-aside__header">
          <span className="latest-news-aside__eyebrow" aria-hidden="true">
            Rádio Nacional
          </span>
          <h2 className="latest-news-aside__title">{title}</h2>
        </header>

        {loading ? (
          <div className="latest-news-aside__list latest-news-aside__list--loading">
            {Array.from({ length: maxItems }).map((_, index) => (
              <LatestNewsAsideSkeletonItem key={index} index={index} />
            ))}
          </div>
        ) : items.length ? (
          <div className="latest-news-aside__list">
            {items.map((item, index) => {
              const url = item?.['@id'] || '';
              const itemTitle = item?.title || '';

              return (
                <article
                  key={item?.['@id'] || itemTitle}
                  className="latest-news-aside__item"
                >
                  <UniversalLink
                    href={url}
                    className="latest-news-aside__link pressable"
                    title={itemTitle}
                  >
                    <span
                      className="latest-news-aside__index"
                      aria-hidden="true"
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="latest-news-aside__item-title">
                      {itemTitle}
                    </h3>
                    <span
                      className="latest-news-aside__arrow"
                      aria-hidden="true"
                    />
                  </UniversalLink>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="latest-news-aside__empty">
            Nenhuma notícia disponível no momento.
          </div>
        )}
      </div>
    </section>
  );
}
